import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Alert from "../common/Alert";
import { getErrorMessage } from "../../utils/errorMessage";
import { normalizeMoneyAmount } from "../../utils/money";
import { SHOW_TIP_UI } from "../../utils/uiFlags";

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatAmountText = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0.00";
  return numeric.toFixed(2);
};

const sanitizeAmountText = (value) => {
  const raw = String(value ?? "").replace(/[^\d.]/g, "");
  if (!raw) return "";

  const firstDot = raw.indexOf(".");
  if (firstDot === -1) {
    return raw.replace(/^0+(?=\d)/, "") || "0";
  }

  const integerPart = raw.slice(0, firstDot).replace(/^0+(?=\d)/, "") || "0";
  const decimalPart = raw
    .slice(firstDot + 1)
    .replace(/\./g, "")
    .slice(0, 2);

  return `${integerPart}.${decimalPart}`;
};

const applyAmountToken = (current, token) => {
  const cleaned = sanitizeAmountText(current);

  if (token === "BACK") {
    return cleaned.slice(0, -1);
  }

  if (token === "C") {
    return "";
  }

  if (token === "FULL") {
    return "";
  }

  if (token === "10") {
    return formatAmountText(Number(cleaned || 0) + 10);
  }

  if (token === "20") {
    return formatAmountText(Number(cleaned || 0) + 20);
  }

  if (token === ".") {
    if (cleaned.includes(".")) return cleaned;
    return cleaned ? `${cleaned}.` : "0.";
  }

  if (token === "00") {
    const base = cleaned || "0";
    return sanitizeAmountText(`${base}00`);
  }

  const next = `${cleaned}${token}`;
  return sanitizeAmountText(next);
};

const MethodCard = ({
  label,
  helper,
  tone,
  icon,
  selected,
  processing,
  disabled,
  onClick,
}) => {
  const toneClasses = {
    emerald: {
      selected: "border-emerald-300 bg-emerald-50 text-emerald-800",
      idle: "border-slate-200 bg-white text-slate-900 hover:border-emerald-300 hover:bg-emerald-50",
      dot: "bg-emerald-500 text-white",
      iconBg: "bg-emerald-100",
      helper: "text-emerald-700",
    },
    indigo: {
      selected: "border-indigo-300 bg-indigo-50 text-indigo-800",
      idle: "border-slate-200 bg-white text-slate-900 hover:border-indigo-300 hover:bg-indigo-50",
      dot: "bg-indigo-500 text-white",
      iconBg: "bg-indigo-100",
      helper: "text-indigo-700",
    },
  };

  const theme = toneClasses[tone] || toneClasses.indigo;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full rounded-xl border-2 p-2.5 text-left shadow-sm transition",
        selected ? theme.selected : theme.idle,
        processing ? "opacity-90" : "",
        disabled ? "cursor-not-allowed opacity-60" : "",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        <div
          className={[
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            theme.iconBg,
          ].join(" ")}
        >
          {processing ? (
            <div
              className={[
                "h-6 w-6 animate-spin rounded-full border-2 border-t-transparent",
                tone === "emerald" ? "border-emerald-500" : "border-indigo-500",
              ].join(" ")}
            />
          ) : (
            icon
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="text-base font-bold">{label}</div>
            {selected && !processing && (
              <div
                className={[
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                  theme.dot,
                ].join(" ")}
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}
          </div>
          <div className={["mt-0.5 text-xs sm:text-sm", theme.helper].join(" ")}>
            {helper}
          </div>
        </div>
      </div>
    </button>
  );
};

const KeyButton = ({ label, onClick, tone = "default", className = "" }) => {
  const toneClasses = {
    default:
      "border-slate-200 bg-white text-slate-900 hover:bg-slate-50",
    action:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    danger:
      "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "h-11 rounded-xl border text-sm font-semibold transition active:scale-[0.98]",
        toneClasses[tone] || toneClasses.default,
        className,
      ].join(" ")}
    >
      {label}
    </button>
  );
};

const ProcessPaymentModal = ({
  isOpen,
  onClose,
  title = "Process Payment",
  subtotal = 0,
  tax = 0,
  total = 0,
  amountDue = 0,
  isProcessing = false,
  activePaymentMethod = null,
  paymentError = null,
  paymentSuccess = null,
  showAmountEntry = true,
  showKeypad = true,
  tipConfig,
  onCash,
  onCard,
}) => {
  const [selectedMethod, setSelectedMethod] = useState("CASH");
  const amountInputRef = useRef(null);
  const [amountText, setAmountText] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const [tipPercentage, setTipPercentage] = useState(0);
  const [customTipText, setCustomTipText] = useState("");

  const tipEnabled = tipConfig?.enabled ?? true;
  const tipPercentages = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(tipConfig?.percentages)
            ? tipConfig.percentages
            : [15, 18, 20]
          )
            .map(Number)
            .filter(
              (percentage) =>
                Number.isFinite(percentage) && percentage > 0 && percentage <= 100,
            ),
        ),
      ).slice(0, 3),
    [tipConfig?.percentages],
  );

  useEffect(() => {
    if (!isOpen) return;
    setAmountText(
      Number(amountDue || 0) > 0 ? formatAmountText(amountDue) : "",
    );
    setValidationMessage("");
    setTipPercentage(0);
    setCustomTipText("");
  }, [amountDue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedMethod("CASH");
  }, [isOpen]);

  useEffect(() => {
    if (tipEnabled) return;
    setTipPercentage(0);
    setCustomTipText("");
  }, [tipEnabled]);

  useEffect(() => {
    if (!isOpen || isProcessing) return;
    if (Number(amountDue || 0) > 0) return;
    onClose?.();
  }, [amountDue, isOpen, isProcessing, onClose]);

  useEffect(() => {
    if (!isOpen || isProcessing) return;

    const frame = window.requestAnimationFrame(() => {
      const input = amountInputRef.current;
      if (!input) return;
      input.focus?.();
      const caretPosition = input.value.length;
      input.setSelectionRange?.(caretPosition, caretPosition);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, isProcessing]);

  const cleanedAmountText = useMemo(
    () => sanitizeAmountText(amountText),
    [amountText],
  );

  const displayAmount = useMemo(() => cleanedAmountText, [cleanedAmountText]);

  const enteredAmount = useMemo(() => {
    if (!cleanedAmountText) return null;
    const numeric = Number(cleanedAmountText);
    return Number.isFinite(numeric) ? numeric : null;
  }, [cleanedAmountText]);

  const balanceDueAmount = Number(amountDue || 0);
  const expectedAmount = normalizeMoneyAmount(balanceDueAmount);
  const normalizedBalanceDueAmount = normalizeMoneyAmount(balanceDueAmount);
  const shouldShowTipControls =
    SHOW_TIP_UI && tipEnabled && selectedMethod === "CARD";
  const tipAmount = normalizeMoneyAmount(
    shouldShowTipControls
      ? tipPercentage > 0
        ? (normalizedBalanceDueAmount * tipPercentage) / 100
        : Number(customTipText || 0)
      : 0,
  );
  const totalWithTip = normalizeMoneyAmount(Number(total || 0) + tipAmount);
  const enteredAmountRounded =
    enteredAmount === null ? null : normalizeMoneyAmount(enteredAmount);
  const hasOutstandingBalance = balanceDueAmount > 0;
  const enteredAmountExceedsBalance =
    enteredAmountRounded !== null &&
    enteredAmountRounded > normalizedBalanceDueAmount;
  const enteredAmountDoesNotMatchDue =
    enteredAmountRounded !== null && enteredAmountRounded !== expectedAmount;

  const handleClose = () => {
    if (!isProcessing) onClose?.();
  };

  const selectMethod = (method) => {
    if (isProcessing) return;
    setSelectedMethod(method);
    if (method === "CASH") {
      setTipPercentage(0);
      setCustomTipText("");
    }
  };

  const handleKey = (token) => {
    if (isProcessing) return;
    setAmountText((current) => applyAmountToken(current, token));
  };

  const handleAmountChange = (event) => {
    if (isProcessing) return;
    if (validationMessage) setValidationMessage("");
    setAmountText(sanitizeAmountText(event.target.value));
  };

  const handleAddPayment = () => {
    if (isProcessing) return;
    if (validationMessage) setValidationMessage("");
    if (!hasOutstandingBalance) return;
    if (!cleanedAmountText || enteredAmount === null || enteredAmount <= 0) {
      setValidationMessage("Please Enter the Total amount.");
      return;
    }
    if (enteredAmountExceedsBalance) {
      setValidationMessage("Please Enter the Total amount.");
      return;
    }
    if (enteredAmountDoesNotMatchDue) {
      setValidationMessage("Please Enter the Total amount.");
      return;
    }

    if (selectedMethod === "CARD") {
      onCard?.(enteredAmount, tipAmount);
      return;
    }
    onCash?.(enteredAmount, tipAmount);
  };

  const isSelected = (method) => selectedMethod === method;
  const isBusy = (method) => isProcessing && activePaymentMethod === method;
  const paymentErrorMessage = getErrorMessage(
    paymentError,
    "Payment failed. Please try again.",
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="2xl"
      bodyClassName="!overflow-y-auto !p-0"
      panelClassName={showAmountEntry ? "max-w-[920px]" : "max-w-[560px]"}
    >
      <div className="flex flex-col p-3">
        {paymentError && <Alert tone="error">{paymentErrorMessage}</Alert>}
        {paymentSuccess && <Alert tone="success">{paymentSuccess}</Alert>}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-slate-50/80 ring-1 ring-slate-200">
          <div
            className={[
              "grid min-h-0",
              showAmountEntry ? "lg:grid-cols-2" : "grid-cols-1",
            ].join(" ")}
          >
            {showAmountEntry && (
              <div className="flex min-h-0 flex-col border-b border-slate-200 p-3 lg:border-b-0 lg:border-r">
              <div className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                Enter Amount
              </div>

              <div className="mt-2 rounded-xl bg-white px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-slate-500">$</span>
                  <input
                    ref={amountInputRef}
                    type="text"
                    inputMode="decimal"
                    enterKeyHint="done"
                    value={displayAmount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                    autoComplete="off"
                    className="w-full bg-transparent text-[2rem] font-black tracking-tight text-slate-900 outline-none tabular-nums placeholder:text-slate-300"
                  />
                </div>
              </div>

              {validationMessage && (
                <div className="mt-1">
                  <Alert tone="warning">
                    {validationMessage}
                  </Alert>
                </div>
              )}

              {showKeypad && (
                <div className="mt-3 flex min-h-0 flex-col">
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {[
                      ["7", "8", "9"],
                      ["4", "5", "6"],
                      ["1", "2", "3"],
                    ].map((row) =>
                      row.map((key) => (
                        <KeyButton
                          key={key}
                          label={key}
                          onClick={() => handleKey(key)}
                          className="h-12 text-base"
                        />
                      )),
                    )}
                    <KeyButton
                      label="0"
                      onClick={() => handleKey("0")}
                      className="h-12 text-base"
                    />
                    <KeyButton
                      label="."
                      onClick={() => handleKey(".")}
                      className="h-12 text-base"
                    />
                    <KeyButton
                      label="DEL"
                      onClick={() => handleKey("BACK")}
                      className="h-12 text-sm"
                    />
                  </div>
                </div>
              )}
              </div>
            )}

            <div className="flex min-h-0 flex-col p-3">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Order Summary
              </div>
              <div className="mt-2 rounded-xl bg-white p-3 shadow-sm">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-base">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-base">
                    <span className="text-slate-600">Tax</span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(tax)}
                    </span>
                  </div>
                  {shouldShowTipControls && (
                    <div className="flex items-center justify-between text-base">
                      <span className="text-slate-600">Tip</span>
                      <span className="font-medium text-emerald-600">
                        {formatCurrency(tipAmount)}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-base font-semibold text-slate-900">
                        Total
                      </span>
                      <span className="text-base font-bold text-indigo-600">
                        {formatCurrency(totalWithTip)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {shouldShowTipControls && (
                <div className="mt-2.5">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Add Tip
                  </div>
                  <div className="mt-1 grid grid-cols-4 gap-1">
                    {tipPercentages.map((percentage) => (
                      <button
                        key={percentage}
                        type="button"
                        disabled={isProcessing}
                        onClick={() => {
                          setCustomTipText("");
                          setTipPercentage((current) =>
                            current === percentage ? 0 : percentage,
                          );
                        }}
                        className={[
                          "h-8 rounded-lg border text-xs font-bold transition",
                          tipPercentage === percentage
                            ? "border-emerald-500 bg-emerald-500 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50",
                        ].join(" ")}
                      >
                        {percentage}%
                      </button>
                    ))}
                    <div className="relative">
                      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                        $
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label="Custom tip amount"
                        disabled={isProcessing}
                        value={customTipText}
                        onChange={(event) => {
                          setTipPercentage(0);
                          setCustomTipText(sanitizeAmountText(event.target.value));
                        }}
                        placeholder="Custom"
                        className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-5 pr-1 text-xs font-semibold text-slate-900 outline-none focus:border-emerald-400"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-4 flex min-h-0 flex-1 flex-col">
                <div className="mb-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Payment Method
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2.5">
                    <MethodCard
                      label="Cash"
                      helper="Pay with cash"
                      tone="emerald"
                      selected={isSelected("CASH")}
                      processing={isBusy("CASH")}
                      disabled={isProcessing}
                      onClick={() => selectMethod("CASH")}
                      icon={
                        <svg
                          className="h-5 w-5 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      }
                    />
                    <MethodCard
                      label="Card"
                      helper="Credit or debit card"
                      tone="indigo"
                      selected={isSelected("CARD")}
                      processing={isBusy("CARD")}
                      disabled={isProcessing}
                      onClick={() => selectMethod("CARD")}
                      icon={
                        <svg
                          className="h-5 w-5 text-indigo-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                      }
                    />
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="pos-primary"
                  onClick={handleAddPayment}
                  disabled={isProcessing}
                  className="mt-auto h-11 w-full text-base shadow-lg"
                >
                  {isProcessing ? "Processing..." : "Pay"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProcessPaymentModal;
